import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldLogo } from "../components/landing/Logo";
import {
  Mail, Lock, Building2, Phone, ArrowRight, Loader2, 
  Eye, EyeOff, Sparkles, CheckCircle2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrialAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Default to login mode when accessed via /trial/login path
  const [mode, setMode] = useState(
    location.pathname === "/trial/login" ? "login" : "register"
  );
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    email: searchParams.get("email") || "",
    password: "",
    restaurantName: searchParams.get("restaurant") || "",
    phone: searchParams.get("phone") || "",
    notificationPreference: "email",
    marketingConsent: false  // S001-T11: CASL explicit opt-in
  });

  // Get context from URL params (passed from savings report)
  const fromChatSession = searchParams.get("session");
  const savingsReportId = searchParams.get("report");

  // Check if already logged in
  useEffect(() => {
    const token = sessionStorage.getItem("argus_trial_token");
    if (token) {
      navigate("/trial/dashboard");
    }
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (mode === "register" && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      if (mode === "register") {
        const res = await axios.post(`${API}/trial/register`, {
          email: formData.email,
          password: formData.password,
          restaurant_name: formData.restaurantName || undefined,
          phone: formData.phone || undefined,
          notification_preference: formData.notificationPreference,
          from_chat_session: fromChatSession || undefined,
          savings_report_id: savingsReportId || undefined,
          marketing_consent: formData.marketingConsent
        });
        
        // Store token
        sessionStorage.setItem("argus_trial_token", res.data.trial_token);
        sessionStorage.setItem("argus_trial_user_id", res.data.user_id);
        sessionStorage.setItem("argus_trial_email", res.data.email);
        
        toast.success("Trial activated! Let's start your tour.");
        
        // Track conversion
        if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
          window.gtag('event', 'trial_registration', {
            'event_category': 'conversion',
            'event_label': formData.email
          });
        }
        
        navigate("/trial/dashboard");
        
      } else {
        const res = await axios.post(`${API}/trial/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // Store token
        sessionStorage.setItem("argus_trial_token", res.data.trial_token);
        sessionStorage.setItem("argus_trial_user_id", res.data.user_id);
        sessionStorage.setItem("argus_trial_email", res.data.email);
        
        if (res.data.is_expired) {
          toast.warning("Your trial has expired! You have 24 hours to become a founder.");
        } else {
          toast.success(`Welcome back! ${res.data.days_remaining} days remaining.`);
        }
        
        navigate("/trial/dashboard");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Something went wrong";
      
      if (errorMessage.includes("already registered")) {
        setMode("login");
        toast.info("Account exists! Please login instead.");
      } else if (errorMessage.includes("Invalid email or password")) {
        setErrors({ password: "Invalid email or password" });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-[#040F49] flex items-center justify-center px-4 py-12">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-lime/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <ShieldLogo size={48} />
            <div className="text-left">
              <span className="font-inter font-black text-gold text-2xl block">Argus Xenia</span>
              <span className="text-sm text-slate-400">Free Trial</span>
            </div>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-[#0A1244] rounded-2xl border border-gold/30 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gold/10 border-b border-gold/20 px-6 py-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-gold" />
              <div>
                <h1 className="font-inter font-bold text-white">
                  {mode === "register" ? "Start Your Free Trial" : "Welcome Back"}
                </h1>
                <p className="text-xs text-slate-400">
                  {mode === "register" 
                    ? "Explore all currently available V1 capabilities. No credit card required."
                    : "Login to continue your trial."}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-slate-300 text-sm">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="you@restaurant.com"
                  className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold"
                  data-testid="trial-email-input"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-slate-300 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  placeholder={mode === "register" ? "Create a password (6+ chars)" : "Enter your password"}
                  className="pl-10 pr-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold"
                  data-testid="trial-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.password}
                </p>
              )}
            </div>

            {/* Registration-only fields */}
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="restaurant" className="text-slate-300 text-sm">Restaurant Name (Optional)</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="restaurant"
                      type="text"
                      value={formData.restaurantName}
                      onChange={(e) => handleInputChange("restaurantName", e.target.value)}
                      placeholder="Your Restaurant Name"
                      className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-300 text-sm">Phone (Optional, for reminders)</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold"
                    />
                  </div>
                </div>

                {/* Notification preference */}
                <div>
                  <Label className="text-slate-300 text-sm">How should we remind you?</Label>
                  <div className="flex gap-2 mt-2">
                    {[
                      { value: "email", label: "Email" },
                      { value: "sms", label: "SMS" },
                      { value: "both", label: "Both" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleInputChange("notificationPreference", opt.value)}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-all ${
                          formData.notificationPreference === opt.value
                            ? "bg-gold/20 border-gold text-gold"
                            : "bg-indigo-700/20 border-slate-600 text-slate-400 hover:border-slate-500"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* S001-T11: CASL marketing consent checkbox */}
                <div className="flex items-start gap-3 pt-1">
                  <input
                    id="marketing-consent"
                    type="checkbox"
                    checked={formData.marketingConsent}
                    onChange={(e) => handleInputChange("marketingConsent", e.target.checked)}
                    data-testid="marketing-consent-checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-gold/40 bg-indigo-700/30 accent-gold cursor-pointer flex-shrink-0"
                  />
                  <Label
                    htmlFor="marketing-consent"
                    className="text-xs text-slate-400 leading-relaxed cursor-pointer"
                  >
                    I agree to receive product updates, tips, and offers from Argus Xenia by email. You can unsubscribe at any time. (Optional)
                  </Label>
                </div>
              </>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gold hover:bg-gold/90 text-indigo-700 font-bold py-3 mt-4"
              data-testid="trial-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {mode === "register" ? "Creating Trial..." : "Logging in..."}
                </>
              ) : (
                <>
                  {mode === "register" ? "Start My Free Trial" : "Login to Trial"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {/* Toggle mode */}
            <div className="text-center pt-4 border-t border-slate-700/50">
              <p className="text-sm text-slate-400">
                {mode === "register" ? "Already have an account?" : "Don't have an account?"}
                <button
                  type="button"
                  onClick={() => setMode(mode === "register" ? "login" : "register")}
                  className="text-gold hover:underline ml-2"
                >
                  {mode === "register" ? "Login" : "Start Free Trial"}
                </button>
              </p>
            </div>
          </form>

          {/* Trial benefits */}
          {mode === "register" && (
            <div className="bg-lime/5 border-t border-lime/20 px-6 py-4">
              <p className="text-xs text-lime font-medium mb-2">What you get in your trial:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "AI Phone Receptionist",
                  "Vision AI Analysis",
                  "Voice SOP Builder",
                  "Full Analytics Dashboard"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-slate-400">
                    <CheckCircle2 className="w-3 h-3 text-lime" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-300">
            ← Back to landing page
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
